import React, { useMemo, useState } from "react";
import { FieldInput, FieldTextArea, FieldJSON, FieldSection, FieldButton } from "@/components/ui/Field";
import { useGolemErrors } from "@/lib/golem/useGolemErrors";
import { type APIError } from "@/api/APIError";
import { ErrorBox } from "@/components/ui/ErrorBox";

type PartnerAppFormValues = {
    name: string;
    description: string;
    notificationEmail: string;
    scope: string;
    requireSigning: boolean;
    rollAfterDays: string;
    rolloverDays: string;
    codeLength: string;
    codeFormat: "numeric" | "alphanumeric";
};

type PartnerAppFormProps = {
    initial?: Partial<PartnerAppFormValues>;
    submitLabel: string;
    onSubmit: (values: PartnerAppFormValues) => Promise<void>;
    mode?: "create" | "edit";
};

export type { PartnerAppFormValues };

export function PartnerAppForm({ initial, submitLabel, onSubmit, mode = "create" }: PartnerAppFormProps) {
    const golemErrors = useGolemErrors();
    const [name, setName] = useState(initial?.name ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [notificationEmail, setNotificationEmail] = useState(initial?.notificationEmail ?? "");
    const [scope, setScope] = useState(initial?.scope ?? "{}");
    const [requireSigning, setRequireSigning] = useState(initial?.requireSigning ?? false);
    const [rollAfterDays, setRollAfterDays] = useState(initial?.rollAfterDays ?? "0");
    const [rolloverDays, setRolloverDays] = useState(initial?.rolloverDays ?? "0");
    const [codeLength, setCodeLength] = useState(initial?.codeLength ?? "8");
    const [codeFormat, setCodeFormat] = useState<"numeric" | "alphanumeric">(initial?.codeFormat ?? "numeric");
    const previewChars = useMemo(() => {
        const charset = codeFormat === "alphanumeric"
            ? "23456789ABCDEFGHJKLMNPQRSTUVWXYZ"
            : "0123456789";
        return Array.from({ length: 24 }, () => charset[Math.floor(Math.random() * charset.length)]);
    }, [codeFormat]);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<APIError | null>(null);

    const handleSubmit = async () => {
        setError(null);
        golemErrors.clearErrors();
        setSubmitting(true);
        try {
            await onSubmit({ name, description, notificationEmail, scope, requireSigning, rollAfterDays, rolloverDays, codeLength, codeFormat });
        } catch (e: unknown) {
            const err = e as APIError;
            golemErrors.setFromError(err);
            setError(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="bg-white shadow-md rounded-md p-6 flex flex-col gap-6">
                <FieldSection title="App Details" first>
                    <FieldInput label="Name" value={name} onChange={setName} errors={golemErrors} field="name" placeholder="App name" required />
                    <FieldTextArea label="Description" value={description} onChange={setDescription} errors={golemErrors} field="description" placeholder="Optional description" rows={3} maxRows={12} />
                    <FieldInput label="Notification Email" value={notificationEmail} onChange={setNotificationEmail} errors={golemErrors} field="notificationEmail" placeholder="credentials@example.com" required />
                    <FieldJSON label="Scope" value={scope} onChange={setScope} errors={golemErrors} field="scope" showPaths />
                </FieldSection>

                {mode === "create" && <FieldSection title="Portal Code">
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Length</span>
                            <span className="text-sm font-semibold text-dark-dark-grey bg-desaturated-grey px-2.5 py-0.5 rounded-full tabular-nums">
                                {codeLength} characters
                            </span>
                        </div>
                        <input
                            type="range"
                            min={6}
                            max={24}
                            step={1}
                            value={codeLength}
                            onChange={e => setCodeLength(e.target.value)}
                            className="w-full cursor-pointer appearance-none h-1.5 rounded-full bg-light-light-grey
                                [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue
                                [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm
                                [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
                                [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue
                                [&::-moz-range-thumb]:cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-light-grey select-none">
                            <span>6</span>
                            <span>24</span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Format</span>
                        <div className="grid grid-cols-2 gap-2">
                            {([
                                { value: "numeric" as const, label: "Numeric", desc: "Digits 0–9 only" },
                                { value: "alphanumeric" as const, label: "Alphanumeric", desc: "Letters & digits, no ambiguous characters" },
                            ]).map(opt => (
                                <label
                                    key={opt.value}
                                    className={`flex items-center gap-3 cursor-pointer rounded-lg border p-3 transition-all select-none ${
                                        codeFormat === opt.value
                                            ? "border-blue/50 bg-blue/[0.04] ring-1 ring-blue/20"
                                            : "border-light-grey hover:border-grey"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="codeFormat"
                                        value={opt.value}
                                        checked={codeFormat === opt.value}
                                        onChange={() => setCodeFormat(opt.value)}
                                        className="sr-only"
                                    />
                                    <div className="flex-1">
                                        <p className={`text-sm font-medium ${codeFormat === opt.value ? "text-blue" : "text-dark-dark-grey"}`}>
                                            {opt.label}
                                        </p>
                                        <p className="text-xs text-grey leading-relaxed mt-0.5">{opt.desc}</p>
                                    </div>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        codeFormat === opt.value ? "border-blue" : "border-light-grey"
                                    }`}>
                                        {codeFormat === opt.value && (
                                            <div className="w-2 h-2 rounded-full bg-blue" />
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Preview</span>
                        <div className="flex flex-wrap gap-1 p-3 bg-glacier-grey rounded-lg border border-smokey-purple/20">
                            {previewChars.slice(0, parseInt(codeLength) || 8).map((char, i) => (
                                <div
                                    key={i}
                                    className="w-6 h-7 rounded bg-white border border-smokey-purple/20 flex items-center justify-center text-titanium-violet/60 text-xs font-mono font-semibold select-none"
                                >
                                    {char}
                                </div>
                            ))}
                        </div>
                    </div>
                </FieldSection>}

                {mode === "create" && <FieldSection title="Credential Settings">
                    <label className={`flex items-center gap-4 cursor-pointer rounded-lg border p-3.5 transition-all select-none ${
                        requireSigning
                            ? "border-blue/50 bg-blue/[0.04] ring-1 ring-blue/20"
                            : "border-light-grey hover:border-grey"
                    }`}>
                        <input type="checkbox" checked={requireSigning} onChange={e => setRequireSigning(e.target.checked)} className="sr-only" />
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${requireSigning ? "text-blue" : "text-dark-dark-grey"}`}>
                                Require request signing
                            </p>
                            <p className="text-xs text-grey leading-relaxed mt-0.5">
                                API requests must include an Ed25519 signature, verifying they originate from the partner.
                            </p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            requireSigning ? "bg-blue border-blue" : "border-light-grey"
                        }`}>
                            {requireSigning && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                                </svg>
                            )}
                        </div>
                    </label>
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Roll After</span>
                                <span className="text-sm font-semibold text-dark-dark-grey bg-desaturated-grey px-2.5 py-0.5 rounded-full tabular-nums">
                                    {parseInt(rollAfterDays) === 0 ? "Disabled" : `${rollAfterDays} days`}
                                </span>
                            </div>
                            <input
                                type="range"
                                min={0}
                                max={365}
                                step={1}
                                value={rollAfterDays}
                                onChange={e => {
                                    let v = parseInt(e.target.value);
                                    if (v > 0 && v < 30) v = 30;
                                    setRollAfterDays(v.toString());
                                    if (v > 0 && parseInt(rolloverDays) === 0) setRolloverDays("7");
                                }}
                                className="w-full cursor-pointer appearance-none h-1.5 rounded-full bg-light-light-grey
                                    [&::-webkit-slider-thumb]:appearance-none
                                    [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue
                                    [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm
                                    [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
                                    [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                                    [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue
                                    [&::-moz-range-thumb]:cursor-pointer"
                            />
                            <div className="flex justify-between text-xs text-light-grey select-none">
                                <span>Disabled</span>
                                <span>365d</span>
                            </div>
                        </div>

                        <div className={`flex flex-col gap-2 transition-opacity ${parseInt(rollAfterDays) === 0 ? "opacity-40 pointer-events-none" : ""}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">Rollover Window</span>
                                <span className="text-sm font-semibold text-dark-dark-grey bg-desaturated-grey px-2.5 py-0.5 rounded-full tabular-nums">
                                    {parseInt(rollAfterDays) === 0 ? "Disabled" : `${rolloverDays} days`}
                                </span>
                            </div>
                            <input
                                type="range"
                                min={7}
                                max={60}
                                step={1}
                                value={Math.max(parseInt(rolloverDays) || 7, 7)}
                                disabled={parseInt(rollAfterDays) === 0}
                                onChange={e => setRolloverDays(e.target.value)}
                                className="w-full cursor-pointer appearance-none h-1.5 rounded-full bg-light-light-grey
                                    [&::-webkit-slider-thumb]:appearance-none
                                    [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue
                                    [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm
                                    [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
                                    [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                                    [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue
                                    [&::-moz-range-thumb]:cursor-pointer"
                            />
                            <div className="flex justify-between text-xs text-light-grey select-none">
                                <span>7d</span>
                                <span>60d</span>
                            </div>
                        </div>
                    </div>
                </FieldSection>}

                <ErrorBox error={error} />
                <div className="flex justify-end">
                    <div className="w-36">
                        <FieldButton text={submitLabel} loading={submitting} onClick={handleSubmit} />
                    </div>
                </div>
            </div>
        </>
    );
}
